import React from "react";
import { useSelector } from "react-redux";
import clsx from "clsx";
import { makeStyles, withStyles } from "@material-ui/core/styles";
import AttachMoneyIcon from "@material-ui/icons/AttachMoney";
import EventAvailableIcon from "@material-ui/icons/EventAvailable";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import { Tooltip, Badge, Divider } from "@material-ui/core";
import Avatar from "@material-ui/core/Avatar";
import PeopleIcon from "@material-ui/icons/People";
import HomeIcon from "@material-ui/icons/Home";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import Drawer from "@material-ui/core/Drawer";

import TokenHelper from "../../../helpers/TokenHelper";
import PermissionService from "../../../services/PermissionService";
import { LogoutQuestion } from "./LogoutQuestion";
import background from "../../../assets/images/sidebar_background.jpg";

import {
  updateMainView,
  updateUserProfileVisibility,
} from "../../../components/common/StoreCommon";
import {
  updateUserSettingsVisibilityInStore,
} from "../../../components/common/StoreSettingsUi";

import { useMenu } from "../../../hooks/useMenu";
import { getUserById } from "../../../store/hub/users";
import { getLoggedInUserId } from "../../../store/hub/loggedInUser";
import { getShowUserProfile } from "../../../store/ui/userProfile";
import { getShowUserSettings } from "../../../store/ui/settings";
import { getMainView } from "../../../store/ui/main";

export default function PrimaryDrawer () {
  const classes = useStyles();
  const [, setSidebarOpen] = React.useState(false);
  const loggedInUserId = useSelector(getLoggedInUserId)();
  const loggedInUser = useSelector(getUserById)(loggedInUserId);
  const showUserProfile = useSelector(getShowUserProfile)();
  const showUserSettings = useSelector(getShowUserSettings)();
  const mainView = useSelector(getMainView)();
  const { anchor, handleClick, handleClose } = useMenu();

  const tokenHelper = new TokenHelper();
  const isAdmin = tokenHelper.isAdminRoleSet();
  const permissionService = new PermissionService();
  const buzzBucksEnabled = permissionService.enableBuzzBucks();
  const hrLeaveEnabled = permissionService.enableHrLeave();

  const hubsSelected =
    mainView === "Hub Overview" || mainView === "Team Overview";

  const sidebarEntries = [
    ...(buzzBucksEnabled ? [{
      text: "BuzzBucks",
      icon: <AttachMoneyIcon />,
      count: 0,
    }] : []),
    ...(hrLeaveEnabled ? [{
      text: "HR Leave",
      icon: <EventAvailableIcon />,
      count: 0,
    }] : []),
  ];

  return (
    <>
      
      <Drawer
        anchor="left"
        variant="permanent"
        className={clsx(classes.drawerClose)}
        classes={{paper: clsx(classes.drawerClose)}}
      >
        {/* Profile — top section matches main nav height (64px) */}
        <List className={classes.topSection} disablePadding>
          <OverrideListItem
            button
            key="User"
            className={classes.topItem}
            style={{ borderRadius: "5px", overflowX: "hidden" }}
            onClick={() => {
              updateUserProfileVisibility(!showUserProfile);
              setSidebarOpen(false);
            }}
          >
            <Tooltip title="My Profile">
              <ListItemIcon className={classes.topItemIcon}>
                <Badge
                  badgeContent={0}
                  color="secondary"
                  overlap="rectangular"
                >
                  <Avatar src={loggedInUser?.ImageSrc} alt="user" />
                </Badge>
              </ListItemIcon>
            </Tooltip>
          </OverrideListItem>
        </List>

        <Divider style={{ background: "rgb(208 215 216)" }} />

        {/* Hub Overview */}
        <List disablePadding style={{ marginTop: 10 }}>
          <OverrideListItem
            button
            selected={hubsSelected}
            className={classes.nested}
            style={{ borderRadius: "5px", overflowX: "hidden" }}
            onClick={() => {
              updateMainView("Hub Overview");
              setSidebarOpen(false);
            }}
          >
            <Tooltip title="Hubs">
              <ListItemIcon>
                <HomeIcon
                  style={{
                    color: hubsSelected ? "white" : "rgb(208 215 216)",
                  }}
                />
              </ListItemIcon>
            </Tooltip>
          </OverrideListItem>
        </List>

        {/* Feature entries (BuzzBucks, HR Leave) */}
        {sidebarEntries.length > 0 ? (
          <List disablePadding>
            {sidebarEntries.map((entry) => {
              const selected = mainView === entry.text;
              return (
                <OverrideListItem
                  selected={selected}
                  style={{
                    margin: "10px 0",
                    borderRadius: "5px",
                    overflowX: "hidden",
                  }}
                  button
                  key={entry.text}
                  onClick={() => {
                    if (entry.text === "BuzzBucks") {
                      updateMainView("BuzzBucks");
                      setSidebarOpen(false);
                    } else if (entry.text === "HR Leave") {
                      updateMainView("HR Leave");
                      setSidebarOpen(false);
                    }
                  }}
                >
                  <Tooltip title={entry.text}>
                    <ListItemIcon
                      className={classes.icon}
                      style={{
                        color: selected ? "white" : "rgb(208 215 216)",
                      }}
                    >
                      <Badge badgeContent={entry.count} color="secondary" overlap="rectangular">
                        {entry.icon}
                      </Badge>
                    </ListItemIcon>
                  </Tooltip>
                </OverrideListItem>
              );
            })}
          </List>
        ) : null}
                  
        {isAdmin && isAdmin === true ? (
          <Divider style={{ background: "rgb(208 215 216)", marginBottom: 10 }} />
          ) 
          : null }
        {isAdmin && isAdmin === true ? (
          <OverrideListItem
            button
            onClick={() => {
              updateUserSettingsVisibilityInStore(!showUserSettings);
              setSidebarOpen(false);
            }}
            className={classes.nested}
            style={{ borderRadius: "5px", overflowX: "hidden" }}
          >
          <Tooltip title="User Management">
            <ListItemIcon>
              <PeopleIcon style={{ color: "rgb(208 215 216)" }} />
            </ListItemIcon>
          </Tooltip>
        </OverrideListItem>
          ) 
          : null }
         
        {/* Log Out (anchored to bottom) */} 
        
        <List className={classes.bottom}>
          <Divider
            style={{ background: "rgb(208 215 216)", marginBottom: "10px" }}
          />
          <OverrideListItem
            button
            key="Logout"
            style={{ borderRadius: "5px", overflowX: "hidden" }}
            onClick={(e) => {
              handleClick(e);
            }}
          >
            <ListItemIcon className={classes.icon}>
              <ExitToAppIcon style={{ color: "rgb(208 215 216)" }} />
            </ListItemIcon>
          </OverrideListItem>
        </List>
       
      </Drawer>

      <LogoutQuestion anchor={anchor} handleClose={handleClose} />
      
    </>
  );
}

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
  },
  drawerClose: {
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: "hidden",
    [theme.breakpoints.only("xs")]: {
      width: 0,
      border: "none",
    },
    [theme.breakpoints.up("sm")]: {
      width: 65,
      padding: "0 5px",
    },
    backgroundColor: "rgb(63 68 66)",
    color: "rgb(208 215 216)",
    backgroundImage: `url(${background})`,
    backgroundBlendMode: "multiply",
    backgroundPosition: "right",
  },
  topSection: {
    height: 64,
    minHeight: 64,
    maxHeight: 64,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxSizing: "border-box",
  },
  topItem: {
    height: "100%",
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
  },
  topItemIcon: {
    minWidth: 0,
    margin: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  bottom: {
    marginTop: "auto",
  },
  icon: {
    marginTop: "5px",
    paddingLeft: "5px",
  },
  nested: {
    color: "rgb(208 215 216)",
    paddingLeft: "20px",
  },
}));

const OverrideListItem = withStyles({
  root: {
    "&:hover": {
      backgroundColor: "#666767",
    },
    "&$selected": {
      backgroundColor: "#3f90e2",
      color: "white",
    },
    "&$selected:hover": {
      backgroundColor: "#3378bd",
    },
  },
  selected: {},
})(ListItem);
